import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import OrbitReportService from '../../services/orbit/OrbitReportService'
import { buildComparableDraftFromFormPayload, summarizeDraftDiff } from './reportDraftDiff'

function parseIdText(value) {
  return String(value || '')
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item))
}

function serializeIds(value) {
  return Array.isArray(value) ? value.join(', ') : ''
}

function mergeDraftIntoForm(current, draftPayload = {}) {
  return {
    ...current,
    report_template_key: draftPayload.report_template_key || current.report_template_key,
    filter_name: draftPayload.filter_name ?? current.filter_name,
    user_instruction_text: draftPayload.user_instruction_text ?? current.user_instruction_text,
    report_peroid: draftPayload.report_peroid || current.report_peroid,
    sort: draftPayload.sort || current.sort,
    view_mode: draftPayload.view_mode || current.view_mode,
    start_period: draftPayload.start_period ?? current.start_period,
    month_span_forward: draftPayload.month_span_forward ?? current.month_span_forward,
    locations_text: Array.isArray(draftPayload.locations) ? serializeIds(draftPayload.locations) : current.locations_text,
    categories_text: Array.isArray(draftPayload.categories) ? serializeIds(draftPayload.categories) : current.categories_text,
    classes_text: Array.isArray(draftPayload.classes) ? serializeIds(draftPayload.classes) : current.classes_text,
    include_summary: draftPayload.include_summary ?? current.include_summary,
  }
}

function buildInitialForm(defaults = {}) {
  return {
    report_template_key: 'inventory_action',
    filter_name: '',
    user_instruction_text: '',
    report_peroid: defaults.report_peroid || '',
    sort: defaults.sort || 'LOCATION',
    view_mode: defaults.view_mode || 'Retail',
    start_period: defaults.start_period ?? 0,
    month_span_forward: defaults.month_span_forward ?? 3,
    locations_text: defaults.locations_text || '',
    categories_text: defaults.categories_text || '',
    classes_text: defaults.classes_text || '',
    include_summary: defaults.include_summary ?? true,
  }
}

function buildDraftRequestPayload(formState, pageContext) {
  return {
    page_context: pageContext,
    message: formState.user_instruction_text || 'Create a custom inventory action report.',
    report_template_key: formState.report_template_key,
    filter_name: formState.filter_name,
    user_instruction_text: formState.user_instruction_text,
    draft_payload: {
      report_peroid: formState.report_peroid,
      sort: formState.sort,
      view_mode: formState.view_mode,
      start_period: Number(formState.start_period || 0),
      month_span_forward: Number(formState.month_span_forward || 1),
      locations: parseIdText(formState.locations_text),
      categories: parseIdText(formState.categories_text),
      classes: parseIdText(formState.classes_text),
      include_summary: Boolean(formState.include_summary),
    },
  }
}

function getResponseDraftDiff(...responses) {
  return responses.find((response) => response?.draft_diff)?.draft_diff || null
}

const QUICK_ACTIONS = [
  {
    id: 'prepare-starter',
    label: 'Prepare starter draft',
    action: 'prepare',
    message: 'Create an inventory action report for the current client using the current default timeframe and retail view.',
  },
  {
    id: 'switch-cost',
    label: 'Switch to cost view',
    action: 'update',
    message: 'Switch this report draft to cost view.',
  },
  {
    id: 'sort-class',
    label: 'Sort by class',
    action: 'update',
    message: 'Sort this report draft by class view.',
  },
  {
    id: 'expand-six-months',
    label: 'Expand to 6 months',
    action: 'update',
    message: 'Expand this report draft to 6 months.',
  },
  {
    id: 'review-draft',
    label: 'Review current draft',
    action: 'review',
    message: 'Review this draft before I save it.',
  },
]

function OrbitReportDraftAssistant({ client, reportAssistant }) {
  const defaultReportPeriod = reportAssistant?.defaults?.report_peroid || ''
  const defaultSort = reportAssistant?.defaults?.sort || 'LOCATION'
  const defaultViewMode = reportAssistant?.defaults?.view_mode || 'Retail'
  const defaultStartPeriod = reportAssistant?.defaults?.start_period ?? 0
  const defaultMonthSpanForward = reportAssistant?.defaults?.month_span_forward ?? 3
  const defaultIncludeSummary = reportAssistant?.defaults?.include_summary ?? true
  const [form, setForm] = useState(() => buildInitialForm(reportAssistant?.defaults))
  const [draftResponse, setDraftResponse] = useState(null)
  const [reviewResponse, setReviewResponse] = useState(null)
  const [saveResponse, setSaveResponse] = useState(null)
  const [artifactStatus, setArtifactStatus] = useState(null)
  const [hydratedArtifact, setHydratedArtifact] = useState(null)
  const [hydratedBaselineDraft, setHydratedBaselineDraft] = useState(null)
  const [requestError, setRequestError] = useState('')
  const [changeRequest, setChangeRequest] = useState('')
  const [isPreparing, setIsPreparing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isUpdatingDraft, setIsUpdatingDraft] = useState(false)
  const [isReviewingDraft, setIsReviewingDraft] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false)

  useEffect(() => {
    setForm((current) => ({
      ...current,
      ...buildInitialForm({
        report_peroid: defaultReportPeriod,
        sort: defaultSort,
        view_mode: defaultViewMode,
        start_period: defaultStartPeriod,
        month_span_forward: defaultMonthSpanForward,
        include_summary: defaultIncludeSummary,
      }),
      filter_name: current.filter_name,
      user_instruction_text: current.user_instruction_text,
      locations_text: current.locations_text,
      categories_text: current.categories_text,
      classes_text: current.classes_text,
    }))
  }, [defaultIncludeSummary, defaultMonthSpanForward, defaultReportPeriod, defaultSort, defaultStartPeriod, defaultViewMode])

  const selectedArtifact = reportAssistant?.selectedArtifact

  useEffect(() => {
    if (!selectedArtifact?.draft_payload || !selectedArtifact?.filter_id) {
      setHydratedArtifact(null)
      setHydratedBaselineDraft(null)
      return
    }

    const baselineDraft = selectedArtifact.original_draft_payload || selectedArtifact.draft_payload

    setHydratedArtifact(selectedArtifact)
    setHydratedBaselineDraft(baselineDraft)
    setForm((current) => mergeDraftIntoForm(current, selectedArtifact.draft_payload))
    setDraftResponse({
      status: 'artifact_loaded',
      filter_id: selectedArtifact.filter_id,
      assistant_summary: selectedArtifact.assistant_summary,
      draft_payload: selectedArtifact.draft_payload,
      assistant_source: 'saved_filter',
      assistant_missing_fields: [],
      draft_diff: null,
    })
    setReviewResponse(null)
    setSaveResponse(null)
    setRequestError('')
    setArtifactStatus({
      status: selectedArtifact?.report_file ? 'ok' : 'not_generated',
      filter_id: selectedArtifact.filter_id,
      report_file: selectedArtifact.report_file || null,
    })

    if (reportAssistant?.onArtifactHydrated) {
      reportAssistant.onArtifactHydrated(selectedArtifact)
    }
  }, [selectedArtifact])

  const pageContext = reportAssistant?.context?.page_context || {}
  const hierarchyCounts = reportAssistant?.context?.hierarchy_counts || {}
  const supportedTemplates = reportAssistant?.context?.chat_supported_templates || []
  const aiAssistant = reportAssistant?.context?.ai_assistant || {}
  const isAiAssistantEnabled = Boolean(aiAssistant?.enabled)

  const payload = useMemo(() => buildDraftRequestPayload(form, pageContext), [form, pageContext])
  const comparableCurrentDraft = useMemo(() => buildComparableDraftFromFormPayload({
    ...payload.draft_payload,
    report_template_key: form.report_template_key,
    filter_name: form.filter_name,
  }), [form.filter_name, form.report_template_key, payload.draft_payload])
  const liveDraftDiff = useMemo(() => summarizeDraftDiff(hydratedBaselineDraft, comparableCurrentDraft), [hydratedBaselineDraft, comparableCurrentDraft])
  const responseDraftDiff = getResponseDraftDiff(saveResponse, reviewResponse, draftResponse)
  const displayedDraftDiff = liveDraftDiff || responseDraftDiff

  const updateField = (field) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value
    setForm((current) => ({ ...current, [field]: value }))
  }

  const handlePrepareDraft = async (options = {}) => {
    if (!reportAssistant?.clientId) {
      return
    }

    const overrideForm = options?.overrideForm || null
    const nextForm = overrideForm ? { ...form, ...overrideForm } : form
    const nextPayload = buildDraftRequestPayload(nextForm, pageContext)

    setRequestError('')
    setSaveResponse(null)
    setReviewResponse(null)
    setArtifactStatus(null)
    setIsPreparing(true)
    try {
      if (overrideForm) {
        setForm(nextForm)
      }
      const response = await OrbitReportService.createReportDraft(reportAssistant.clientId, {
        ...nextPayload,
        use_ai_interpretation: isAiAssistantEnabled,
      })
      setDraftResponse(response)
      if (response?.draft_payload) {
        setForm((current) => mergeDraftIntoForm(current, response.draft_payload))
      }
      if (reportAssistant?.onDraftPrepared) {
        reportAssistant.onDraftPrepared(response, nextPayload)
      }
    } catch (error) {
      const nextMessage = error?.response?.data?.message || 'Unable to prepare the custom report draft right now.'
      const errorMessage = Array.isArray(nextMessage) ? JSON.stringify(nextMessage) : String(nextMessage)
      setRequestError(errorMessage)
      if (reportAssistant?.onRequestError) {
        reportAssistant.onRequestError({ phase: 'prepare', message: errorMessage })
      }
    } finally {
      setIsPreparing(false)
    }
  }

  const handleSaveDraft = async () => {
    if (!reportAssistant?.clientId) {
      return
    }

    const currentFilterId = hydratedArtifact?.filter_id || reportAssistant?.selectedArtifact?.filter_id || null

    setRequestError('')
    setIsSaving(true)
    try {
      const response = await OrbitReportService.saveReportDraft(reportAssistant.clientId, {
        ...payload,
        filter_id: currentFilterId,
        original_draft_payload: hydratedBaselineDraft || undefined,
      })
      setSaveResponse(response)
      setDraftResponse(response)
      setReviewResponse(null)
      if (response?.filter_id) {
        const nextBaselineDraft = response?.draft_payload || hydratedBaselineDraft || null
        setHydratedArtifact((current) => ({
          ...(current || {}),
          ...response,
          filter_id: response.filter_id,
          filter_name: response?.artifact?.filter_name || current?.filter_name,
          draft_payload: response?.draft_payload || current?.draft_payload,
          original_draft_payload: nextBaselineDraft,
          report_file: response?.report_file || null,
        }))
        setHydratedBaselineDraft(nextBaselineDraft)
      }
      setArtifactStatus({
        status: response?.report_file ? 'ok' : 'not_generated',
        filter_id: response?.filter_id,
        report_file: response?.report_file || null,
      })
      if (reportAssistant?.onDraftSaved) {
        reportAssistant.onDraftSaved(response, {
          ...payload,
          filter_id: currentFilterId,
          original_draft_payload: hydratedBaselineDraft || undefined,
        })
      }
      if (reportAssistant?.onSaved) {
        reportAssistant.onSaved(response)
      }
    } catch (error) {
      const nextMessage = error?.response?.data?.message || 'Unable to save the custom report artifact right now.'
      const errorMessage = Array.isArray(nextMessage) ? JSON.stringify(nextMessage) : String(nextMessage)
      setRequestError(errorMessage)
      if (reportAssistant?.onRequestError) {
        reportAssistant.onRequestError({ phase: 'save', message: errorMessage })
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleApplyDraftChange = async (overrideChangeRequest = '') => {
    const requestText = String(overrideChangeRequest || changeRequest || '').trim()
    if (!reportAssistant?.clientId || !requestText) {
      return
    }

    setRequestError('')
    setReviewResponse(null)
    setIsUpdatingDraft(true)
    try {
      if (overrideChangeRequest) {
        setChangeRequest(requestText)
      }
      const response = await OrbitReportService.updateReportDraft(reportAssistant.clientId, {
        ...payload,
        message: requestText,
        original_draft_payload: hydratedBaselineDraft || undefined,
      })
      setDraftResponse(response)
      if (response?.draft_payload) {
        setForm((current) => mergeDraftIntoForm(current, response.draft_payload))
      }
      if (reportAssistant?.onDraftUpdated) {
        reportAssistant.onDraftUpdated(response, {
          changeRequest: requestText,
          payload,
        })
      }
    } catch (error) {
      const nextMessage = error?.response?.data?.message || 'Unable to apply the report change request right now.'
      const errorMessage = Array.isArray(nextMessage) ? JSON.stringify(nextMessage) : String(nextMessage)
      setRequestError(errorMessage)
      if (reportAssistant?.onRequestError) {
        reportAssistant.onRequestError({ phase: 'update', message: errorMessage })
      }
    } finally {
      setIsUpdatingDraft(false)
    }
  }

  const handleReviewDraft = async (reviewRequestText = 'Review this draft before I save it.') => {
    if (!reportAssistant?.clientId) {
      return
    }

    setRequestError('')
    setIsReviewingDraft(true)
    try {
      const response = await OrbitReportService.reviewReportDraft(reportAssistant.clientId, {
        ...payload,
        original_draft_payload: hydratedBaselineDraft || undefined,
      })
      setReviewResponse(response)
      if (response?.draft_payload) {
        setForm((current) => mergeDraftIntoForm(current, response.draft_payload))
      }
      if (reportAssistant?.onDraftReviewed) {
        reportAssistant.onDraftReviewed(response, {
          requestPayload: {
            ...payload,
            original_draft_payload: hydratedBaselineDraft || undefined,
          },
          reviewRequestText,
        })
      }
    } catch (error) {
      const nextMessage = error?.response?.data?.message || 'Unable to review the draft right now.'
      const errorMessage = Array.isArray(nextMessage) ? JSON.stringify(nextMessage) : String(nextMessage)
      setRequestError(errorMessage)
      if (reportAssistant?.onRequestError) {
        reportAssistant.onRequestError({ phase: 'review', message: errorMessage })
      }
    } finally {
      setIsReviewingDraft(false)
    }
  }

  const handleQuickAction = async (quickAction) => {
    if (!quickAction?.action) {
      return
    }

    if (quickAction.action === 'prepare') {
      await handlePrepareDraft({
        overrideForm: {
          user_instruction_text: quickAction.message,
        },
      })
      return
    }

    if (quickAction.action === 'update') {
      await handleApplyDraftChange(quickAction.message)
      return
    }

    if (quickAction.action === 'review') {
      await handleReviewDraft(quickAction.message)
    }
  }

  const activeFilterId = saveResponse?.filter_id || hydratedArtifact?.filter_id || draftResponse?.filter_id || artifactStatus?.filter_id || null
  const activeReportFile = artifactStatus?.report_file || null
  const activeReportFileStatus = activeReportFile?.status || null

  const handleGenerateArtifact = async () => {
    if (!reportAssistant?.clientId || !activeFilterId) {
      return
    }

    setRequestError('')
    setIsGenerating(true)
    try {
      const response = await OrbitReportService.generateReportArtifact(reportAssistant.clientId, activeFilterId)
      setArtifactStatus({
        status: response?.status || 'generation_started',
        filter_id: response?.filter_id || activeFilterId,
        report_file: response?.report_file || null,
      })
      if (reportAssistant?.onArtifactGenerationStarted) {
        reportAssistant.onArtifactGenerationStarted(response, {
          filterId: activeFilterId,
        })
      }
    } catch (error) {
      const nextMessage = error?.response?.data?.message || 'Unable to start report generation right now.'
      const errorMessage = Array.isArray(nextMessage) ? JSON.stringify(nextMessage) : String(nextMessage)
      setRequestError(errorMessage)
      if (reportAssistant?.onRequestError) {
        reportAssistant.onRequestError({ phase: 'generate', message: errorMessage })
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handleRefreshArtifactStatus = async () => {
    if (!reportAssistant?.clientId || !activeFilterId) {
      return
    }

    setRequestError('')
    setIsRefreshingStatus(true)
    try {
      const response = await OrbitReportService.getReportArtifactStatus(reportAssistant.clientId, activeFilterId)
      setArtifactStatus(response)
      if (reportAssistant?.onArtifactStatusRefreshed) {
        reportAssistant.onArtifactStatusRefreshed(response, {
          filterId: activeFilterId,
        })
      }
    } catch (error) {
      const nextMessage = error?.response?.data?.message || 'Unable to refresh report status right now.'
      const errorMessage = Array.isArray(nextMessage) ? JSON.stringify(nextMessage) : String(nextMessage)
      setRequestError(errorMessage)
      if (reportAssistant?.onRequestError) {
        reportAssistant.onRequestError({ phase: 'status', message: errorMessage })
      }
    } finally {
      setIsRefreshingStatus(false)
    }
  }

  const handleDownloadArtifact = () => {
    if (!reportAssistant?.clientId || !activeFilterId) {
      return
    }

    window.open(OrbitReportService.getReportArtifactDownloadUrl(reportAssistant.clientId, activeFilterId), '_blank', 'noopener,noreferrer')
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" sx={{ color: '#5f5670', lineHeight: 1.7 }}>
          Page-aware assistant for the Report Library. This first slice creates Inventory Action custom report filters and saves them directly into the existing Report Library artifact model.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`Tab: ${pageContext.tab || 'custom'}`} sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
        <Chip size="small" label={`Templates: ${supportedTemplates.length || 1}`} sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
        <Chip size="small" label={`Saved custom filters: ${reportAssistant?.context?.saved_custom_filter_count ?? 0}`} sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
        <Chip size="small" label={`AI assist: ${isAiAssistantEnabled ? 'on' : 'off'}`} sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
      </Stack>

      {reportAssistant?.isLoading && (
        <Alert severity="info" icon={<CircularProgress size={16} />}>
          Loading report-aware page context…
        </Alert>
      )}

      {!reportAssistant?.isLoading && reportAssistant?.error && (
        <Alert
          severity="warning"
          action={reportAssistant?.onRefreshContext ? <Button color="inherit" size="small" onClick={reportAssistant.onRefreshContext}>Retry</Button> : null}
        >
          {reportAssistant.error}
        </Alert>
      )}

      <Alert severity="info">
        <strong>{client?.Name || 'Current client'}</strong>
        {' '}has {hierarchyCounts.locations ?? 0} locations, {hierarchyCounts.categories ?? 0} categories, and {hierarchyCounts.classes ?? 0} parent classes available for report scoping.
      </Alert>

      <Box>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b5a7f' }}>
          Quick actions
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {QUICK_ACTIONS.map((quickAction) => (
            <Chip
              key={quickAction.id}
              clickable
              label={quickAction.label}
              onClick={() => handleQuickAction(quickAction)}
              disabled={isPreparing || isSaving || isUpdatingDraft || isReviewingDraft || reportAssistant?.isLoading}
              sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }}
            />
          ))}
        </Stack>
      </Box>

      <Stack spacing={1.5}>
        <TextField
          select
          fullWidth
          label="Chat-supported template"
          value={form.report_template_key}
          onChange={updateField('report_template_key')}
          helperText="This first thin slice is intentionally bounded to Inventory Action."
        >
          <MenuItem value="inventory_action">Inventory Action</MenuItem>
        </TextField>

        <TextField
          fullWidth
          label="Custom report name"
          value={form.filter_name}
          onChange={updateField('filter_name')}
          placeholder="Inventory Action - Summer stores"
        />

        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Natural-language instructions"
          value={form.user_instruction_text}
          onChange={updateField('user_instruction_text')}
          placeholder="Example: Focus on retail view for locations 101 and 102 and surface class-level issues for the next 3 months."
        />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            label="Report period"
            value={form.report_peroid}
            onChange={updateField('report_peroid')}
            placeholder="06/2026"
          />
          <TextField select fullWidth label="Sort" value={form.sort} onChange={updateField('sort')}>
            <MenuItem value="LOCATION">By Location</MenuItem>
            <MenuItem value="CLASS">By Class</MenuItem>
            <MenuItem value="EXECUTIVE">By Executive</MenuItem>
          </TextField>
          <TextField select fullWidth label="View mode" value={form.view_mode} onChange={updateField('view_mode')}>
            <MenuItem value="Retail">Retail</MenuItem>
            <MenuItem value="Cost">Cost</MenuItem>
          </TextField>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            type="number"
            label="Starting period offset"
            inputProps={{ min: 0, max: 11 }}
            value={form.start_period}
            onChange={updateField('start_period')}
            helperText="0 means start at the selected report period."
          />
          <TextField
            fullWidth
            type="number"
            label="Months forward"
            inputProps={{ min: 1, max: 12 }}
            value={form.month_span_forward}
            onChange={updateField('month_span_forward')}
          />
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            label="Location ids"
            value={form.locations_text}
            onChange={updateField('locations_text')}
            placeholder="101, 102"
            helperText="Optional comma-separated ids. Leave blank for full client scope."
          />
          <TextField
            fullWidth
            label="Category ids"
            value={form.categories_text}
            onChange={updateField('categories_text')}
            placeholder="330"
          />
          <TextField
            fullWidth
            label="Class ids"
            value={form.classes_text}
            onChange={updateField('classes_text')}
            placeholder="2201, 2202"
          />
        </Stack>

        <FormControlLabel
          control={<Switch checked={Boolean(form.include_summary)} onChange={updateField('include_summary')} />}
          label="Include summary in the saved filter"
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        <Button
          variant="outlined"
          onClick={handlePrepareDraft}
          disabled={isPreparing || isSaving || isUpdatingDraft || isReviewingDraft || reportAssistant?.isLoading}
        >
          {isPreparing ? 'Preparing draft…' : isAiAssistantEnabled ? 'Interpret with AI' : 'Prepare draft'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveDraft}
          disabled={isPreparing || isSaving || isUpdatingDraft || isReviewingDraft || reportAssistant?.isLoading}
          sx={{
            textTransform: 'none',
            borderRadius: 999,
            px: 2,
            background: 'linear-gradient(135deg, #5e3d79 0%, #7d58a5 100%)',
          }}
        >
          {isSaving ? 'Saving report…' : 'Save custom report'}
        </Button>
      </Stack>

      {isAiAssistantEnabled && (
        <Stack spacing={1.25}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Change request"
            value={changeRequest}
            onChange={(event) => setChangeRequest(event.target.value)}
            placeholder="Example: Switch this draft to cost view for the next 6 months and sort by class."
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant="outlined"
              onClick={() => handleApplyDraftChange()}
              disabled={isUpdatingDraft || isPreparing || isSaving || !changeRequest.trim()}
            >
              {isUpdatingDraft ? 'Applying change…' : 'Apply AI change request'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleReviewDraft()}
              disabled={isReviewingDraft || isPreparing || isSaving}
            >
              {isReviewingDraft ? 'Reviewing draft…' : 'Review draft'}
            </Button>
          </Stack>
        </Stack>
      )}

      {requestError && <Alert severity="error">{requestError}</Alert>}

      {hydratedArtifact?.filter_id && (
        <Alert
          severity="info"
          action={reportAssistant?.onClearSelectedArtifact ? <Button color="inherit" size="small" onClick={reportAssistant.onClearSelectedArtifact}>Clear</Button> : null}
        >
          <Typography variant="body2">
            Loaded saved filter <strong>#{hydratedArtifact.filter_id}</strong> into the assistant. You can review it, apply changes, or regenerate the current report artifact.
          </Typography>
        </Alert>
      )}

      {hydratedArtifact?.filter_id && displayedDraftDiff && (
        <Alert severity={displayedDraftDiff?.has_changes ? 'warning' : 'success'}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Edit summary
          </Typography>
          <Typography variant="body2">{displayedDraftDiff.summary}</Typography>
          {displayedDraftDiff?.change_items?.length > 0 && (
            <Stack component="ul" spacing={0.75} sx={{ mt: 1, pl: 2.25, mb: 0 }}>
              {displayedDraftDiff.change_items.map((item) => (
                <Typography component="li" variant="body2" key={`${item.section}-${item.label}`}>
                  <strong>{item.label}:</strong> {item.before || '—'} → {item.after || '—'}
                </Typography>
              ))}
            </Stack>
          )}
        </Alert>
      )}

      {draftResponse?.assistant_summary && (
        <Alert severity={saveResponse?.filter_id ? 'success' : 'info'}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Assistant summary
          </Typography>
          <Typography variant="body2">{draftResponse.assistant_summary}</Typography>
          {draftResponse?.assistant_source && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Source: <strong>{draftResponse.assistant_source}</strong>
            </Typography>
          )}
          {draftResponse?.assistant_warning && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {draftResponse.assistant_warning}
            </Typography>
          )}
          {draftResponse?.assistant_missing_fields?.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Still needed: {draftResponse.assistant_missing_fields.join(', ')}.
            </Typography>
          )}
          {saveResponse?.filter_id && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {saveResponse?.saved_action === 'updated'
                ? <>Updated existing filter <strong>#{saveResponse.filter_id}</strong>. Regenerate the report file to reflect the latest changes.</>
                : (saveResponse?.saved_action === 'unchanged'
                  ? <>No material changes were detected for existing filter <strong>#{saveResponse.filter_id}</strong>. Regeneration is not required.</>
                  : <>Saved as filter <strong>#{saveResponse.filter_id}</strong>. It will appear in the Custom Reports tab after refresh.</>)}
            </Typography>
          )}
        </Alert>
      )}

      {reviewResponse?.assistant_summary && (
        <Alert severity={reviewResponse?.valid === false ? 'warning' : 'info'}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Draft review
          </Typography>
          <Typography variant="body2">{reviewResponse.assistant_summary}</Typography>
          {reviewResponse?.assistant_review_sections?.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Review sections: {reviewResponse.assistant_review_sections.join(', ')}.
            </Typography>
          )}
          {reviewResponse?.assistant_next_action && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Next action: <strong>{reviewResponse.assistant_next_action}</strong>
            </Typography>
          )}
          {reviewResponse?.draft_diff?.summary && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {reviewResponse.draft_diff.summary}
            </Typography>
          )}
        </Alert>
      )}

      {activeFilterId && (
        <Stack spacing={1.5}>
          <Divider />
          <Box>
            <Typography variant="caption" sx={{ color: '#6b5a7f', display: 'block', mb: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Saved artifact actions
            </Typography>
            <Typography variant="body2" sx={{ color: '#5f5670', lineHeight: 1.7, mb: 1 }}>
              Filter artifact #{activeFilterId} can now be generated through the existing report file pipeline.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button
                variant="outlined"
                onClick={handleGenerateArtifact}
                disabled={isGenerating || isRefreshingStatus}
              >
                {isGenerating ? 'Generating…' : 'Generate report file'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleRefreshArtifactStatus}
                disabled={isRefreshingStatus}
              >
                {isRefreshingStatus ? 'Refreshing…' : 'Refresh status'}
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadArtifact}
                disabled={activeReportFileStatus !== 'GENERATED'}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  px: 2,
                  background: 'linear-gradient(135deg, #5e3d79 0%, #7d58a5 100%)',
                }}
              >
                Download report
              </Button>
            </Stack>
          </Box>

          <Alert severity={activeReportFileStatus === 'GENERATED' ? 'success' : 'info'}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Artifact status
            </Typography>
            <Typography variant="body2">
              {activeReportFile
                ? `Current report file status: ${activeReportFile.status_name || activeReportFile.status}.`
                : 'No generated report file exists yet for this artifact.'}
            </Typography>
          </Alert>
        </Stack>
      )}

      <Divider />

      <Box>
        <Typography variant="caption" sx={{ color: '#6b5a7f', display: 'block', mb: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          What this slice does now
        </Typography>
        <Typography variant="body2" sx={{ color: '#5f5670', lineHeight: 1.7 }}>
          It loads page-aware report context, validates hierarchy ids against the current client, prepares a structured Inventory Action draft, and saves the result as an existing <code>filters</code> artifact.
        </Typography>
      </Box>
    </Stack>
  )
}

export default OrbitReportDraftAssistant
